import { useContext, useMemo } from 'react';
import {
  TEDLRFakturaAttestAttestTypEnum,
  TEDLRFakturaDelAttestMinaStatusEnum,
} from '../../../../../../globalQueryTypes';
import { LoginStateContext } from '../../../../../Login/Contexts/LoginStateContext/LoginStateContext';
import {
  createFormData,
  IFieldValidations,
  IValidateFn,
} from '../../../../_Common/Components/BusinessCollectionComponents/BusinessDataForm/Utils/BusinessDataFormUtils';
import KonteringContext from '../../../../_Common/Components/KonteringManager/KonteringContext';
import { IKodstrang } from '../../../../_Common/Components/KonteringManager/KonteringManager';
import { IFilteredKodbehForKonteringsForslag } from '../../../../_Common/Components/KonteringManager/KonteringManagerUtils';
import { FakturaattestWrapperContext } from '../../../Rutiner/FakturaDel/Contexts/FakturaattestWrapperContext';
import { konteringsforslagWithoutDeletedKontodelar } from '../Components/FakturaDelContextManager/FakturaDelContextManager';
import {
  getChangedForslag,
  IsThereOnlyOneMottagningsAttestant,
  getKodstrangar,
  roundToTwoDecimals,
  convertIKonteringValidationErrorToValidationErrorsType,
  getAllFakturaAttester,
  getOtherBeslutAttestanter,
  iAmBeslutsAttestantOnKontering,
  iAmLastBeslutsAttestant,
  iAmReservForForslag,
  getKonteratBelopp,
} from '../Components/Kontering/Utils/KonteringUtils';
import { LRSysOptionsQuery_db_SYSOptions_All } from '../Queries/LRSysOptionQuery/_QueryTypes/LRSysOptionsQuery';
import {
  FakturaDelQuery_node_TEDLRFakturaDel,
  FakturaDelQuery_node_TEDLRFakturaDel__Associations_Faktura__Associations_Fakturadel,
  FakturaDelQuery_node_TEDLRFakturaDel__Associations_Faktura__Associations_Fakturadel__Associations_Fakturaattest,
  FakturaDelQuery_node_TEDLRFakturaDel__Associations_Faktura__Associations_Konteringsforslag_All,
} from '../Queries/_QueryTypes/FakturaDelQuery';
import { getAktuellFakturaAttest } from '../Utils/FakturadelUtils';

/** Validerar datan i Konteringskortet */
export const useValidateKonteringKombination: () => IValidateFn<FakturaDelQuery_node_TEDLRFakturaDel> =
  () => {
    const konteringContext = useContext(KonteringContext);
    const loginStateContext = useContext(LoginStateContext);
    const fakturaattestContext = useContext(FakturaattestWrapperContext);

    const requireMotKodbeh: boolean = useMemo(() => {
      return (
        fakturaattestContext.sysOptions?.filter(
          (option: LRSysOptionsQuery_db_SYSOptions_All) =>
            option.OptionKey === 'LR_REQUIRE_MOTKODBEH'
        )?.[0]?.OptionData === 'Y'
      );
    }, [fakturaattestContext.sysOptions]);

    return ({ values, autovalues, data, touched }) => {
      const formData = createFormData(values, data);
      let errors: IFieldValidations = [];

      if (konteringContext.konteringValidationQueryLoading) {
        return errors.concat([
          {
            fieldName: '-1',
            message: 'Hämtar kodplan',
            type: 'Error',
          },
        ]);
      }

      const dataFormFakturaAttest = (formData?._Associations as any)
        ?.Fakturaattest;

      const dataFormFakturaDelar: FakturaDelQuery_node_TEDLRFakturaDel__Associations_Faktura__Associations_Fakturadel[] =
        (formData?._Associations as any)?.Faktura?._Associations?.Fakturadel;

      const allaFakturaAttester: FakturaDelQuery_node_TEDLRFakturaDel__Associations_Faktura__Associations_Fakturadel__Associations_Fakturaattest[] =
        getAllFakturaAttester(dataFormFakturaDelar);

      const attestTyp: TEDLRFakturaAttestAttestTypEnum =
        formData?.ATTEST_STATUS === 'BESLUTSATTESTERAS'
          ? formData?.AttestMinaStatus ===
            TEDLRFakturaDelAttestMinaStatusEnum.KONTROLLATTESTERAS
            ? TEDLRFakturaAttestAttestTypEnum.KONTROLLATTEST
            : TEDLRFakturaAttestAttestTypEnum.BESLUTSATTEST
          : TEDLRFakturaAttestAttestTypEnum.MOTTAGNINGSATTEST;

      const minFakturaAttest = getAktuellFakturaAttest(
        dataFormFakturaAttest,
        fakturaattestContext.reservForData,
        loginStateContext.sign,
        attestTyp
      );

      if (
        ![
          TEDLRFakturaDelAttestMinaStatusEnum.BESLUTSATTESTERAS,
          TEDLRFakturaDelAttestMinaStatusEnum.MOTTAGNINGSATTESTERAS,
          TEDLRFakturaDelAttestMinaStatusEnum.KONTROLLATTESTERAS,
        ].includes(formData.AttestMinaStatus as any)
      ) {
        return [];
      }

      const otherBeslutAttestanter = getOtherBeslutAttestanter(
        allaFakturaAttester,
        minFakturaAttest.aktuellFakturaAttest?._Associations?.Attestratt?.id
      );

      const kodplanId =
        formData._Associations?.Faktura?._Associations?.Registrering
          ?._Associations?.Bokfverifikationer?._Associations?.Kodplan?.id;

      const allaForslag: FakturaDelQuery_node_TEDLRFakturaDel__Associations_Faktura__Associations_Konteringsforslag_All[] =
        (
          formData._Associations?.Faktura?._Associations as any
        )?.Konteringsforslag?.filter(
          konteringsforslagWithoutDeletedKontodelar
        ).filter(
          (
            kf: FakturaDelQuery_node_TEDLRFakturaDel__Associations_Faktura__Associations_Konteringsforslag_All
          ) =>
            // Hack för att få bort deletade konteringsförslag (ska rättas "på riktigt" i GR)
            !values._Associations?.Faktura_Values?._Associations?.Konteringsforslag_Delete_Values?.some(
              (dkf: any) => dkf.id === kf.id
            )
        ) ?? [];
      const allaForslagFromQuery: FakturaDelQuery_node_TEDLRFakturaDel__Associations_Faktura__Associations_Konteringsforslag_All[] =
        data?._Associations?.Faktura?._Associations?.Konteringsforslag_All?.filter(
          konteringsforslagWithoutDeletedKontodelar
        ).filter(
          (
            kf: FakturaDelQuery_node_TEDLRFakturaDel__Associations_Faktura__Associations_Konteringsforslag_All
          ) =>
            // Hack för att få bort deletade konteringsförslag (ska rättas "på riktigt" i GR)
            !values._Associations?.Faktura_Values?._Associations?.Konteringsforslag_Delete_Values?.some(
              (dkf: any) => dkf.id === kf.id
            )
        ) ?? [];

      const changedForslag = getChangedForslag(
        values,
        autovalues,
        allaForslag,
        allaForslagFromQuery
      );

      const myKonteringsForslag = allaForslag.filter(forslag => {
        return (
          forslag._Associations?.Fakturaattest?.id ===
          minFakturaAttest?.aktuellFakturaAttest?.id
        );
      });

      const otherKonteringsForslag = allaForslag.filter(forslag => {
        return (
          forslag._Associations?.Fakturaattest?.id !==
          minFakturaAttest?.aktuellFakturaAttest?.id
        );
      });

      const kontrolleraForslagIds =
        formData.AttestMinaStatus ===
        TEDLRFakturaDelAttestMinaStatusEnum.MOTTAGNINGSATTESTERAS
          ? IsThereOnlyOneMottagningsAttestant(
              dataFormFakturaAttest,
              dataFormFakturaDelar
            )
            ? allaForslag.map(forslag => forslag.id)
            : changedForslag.map(forslag => forslag.id)
          : allaForslag
              .filter((kf: any) => {
                const lastBeslut = iAmLastBeslutsAttestant(
                  minFakturaAttest.aktuellFakturaAttest,
                  formData?._Associations?.Faktura?._Associations?.Fakturadel?.flatMap(
                    (curr: any) => curr?._Associations?.Fakturaattest ?? []
                  ),
                  loginStateContext.sign
                );
                const beslut = iAmBeslutsAttestantOnKontering(
                  kf,
                  minFakturaAttest.aktuellFakturaAttest
                );
                const reserv = iAmReservForForslag(
                  loginStateContext.sign,
                  formData,
                  kf
                );
                return lastBeslut || beslut || reserv;
              })
              .map(forslag => forslag.id);

      const allKodstrangar: IKodstrang[] =
        allaForslag.map(getKodstrangar) ?? [];

      const changedKodstrangar: IKodstrang[] =
        changedForslag.map(getKodstrangar) ?? [];

      const fakturaBokfDate = new Date(
        formData._Associations?.Faktura?._Associations?.Registrering?._Associations?.Bokfverifikationer?.DATUM
      );

      const konteratBelopp: number = getKonteratBelopp(allaForslag);

      const roundedVatAmount = roundToTwoDecimals(
        (formData?._Associations?.Faktura?.MomsBelopp2 ?? 0) as number
      );

      const roundedFakturaBelopp = roundToTwoDecimals(
        formData?.FakturaBelopp2 as number
      );

      const fakturaBelopp = roundedFakturaBelopp - roundedVatAmount;

      const konteringsforslagWithKodBeh: IFilteredKodbehForKonteringsForslag[] =
        konteringContext.konteringUtils.getKonteringsForslagWithKodbeh();

      const getKonteringValidationsErrors = (
        kodstrang: IKodstrang,
        validateAll: boolean,
        iAmBeslutsAttestantOnKontering: boolean
      ) => {
        errors = errors.concat(
          konteringContext.konteringUtils
            .validateKonteringForAttest(
              kodplanId ?? '',
              kodstrang,
              fakturaBokfDate,
              attestTyp,
              konteratBelopp,
              fakturaBelopp,
              validateAll,
              iAmBeslutsAttestantOnKontering,
              konteringsforslagWithKodBeh,
              myKonteringsForslag,
              otherKonteringsForslag,
              otherBeslutAttestanter,
              requireMotKodbeh,
              minFakturaAttest
            )
            .map(error =>
              convertIKonteringValidationErrorToValidationErrorsType(
                error,
                kodstrang,
                undefined
              )
            )
        );
      };

      // Kontrollera att raderna har en attestant på sig
      errors = errors.concat(
        konteringContext.konteringUtils.getBeslutsattestantMissingError(
          allaForslag ?? []
        )
      );

      // Kontrollera alla konteringsrader
      const isBeslutAttestant =
        minFakturaAttest.aktuellFakturaAttest?.AttestTyp ===
        TEDLRFakturaAttestAttestTypEnum.BESLUTSATTEST;

      const isMottagningsAttestant =
        attestTyp === TEDLRFakturaAttestAttestTypEnum.MOTTAGNINGSATTEST;

      if (isBeslutAttestant || isMottagningsAttestant) {
        if (allKodstrangar.length === 0 || allKodstrangar == null) {
          errors = errors.concat([
            {
              fieldName: '',
              type: 'Error',
              message: 'Det finns inga konteringsförslag',
            },
          ]);
        } else {
          allKodstrangar.forEach(kodstrang => {
            errors = errors.concat(
              konteringContext.konteringUtils.getKonteringSaknasErrors(
                kodstrang,
                isMottagningsAttestant,
                allaForslag.find(forslag => forslag.id === kodstrang.id),
                minFakturaAttest
              )
            );
          });
        }
      }

      if (
        (isBeslutAttestant ||
          IsThereOnlyOneMottagningsAttestant(
            dataFormFakturaAttest,
            dataFormFakturaDelar
          )) &&
        attestTyp !== TEDLRFakturaAttestAttestTypEnum.KONTROLLATTEST
      ) {
        allKodstrangar?.forEach(kodstrang => {
          getKonteringValidationsErrors(
            kodstrang,
            kontrolleraForslagIds.some(kf => kf === kodstrang.id),
            iAmBeslutsAttestantOnKontering(
              allaForslag.find(kf => kf.id === kodstrang.id),
              minFakturaAttest.aktuellFakturaAttest
            )
          );
        });
      } else {
        changedKodstrangar?.forEach(kodstrang =>
          getKonteringValidationsErrors(kodstrang, true, false)
        );
      }
      return errors;
    };
  };
